import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { LOGO_URL } from "../mock/mock";
import { Instagram, Facebook, Linkedin, Mail } from "lucide-react";
import { OPEN_COOKIE_BANNER_EVENT } from "./CookieBanner";

/* TikTok inline icon (lucide doesn't ship one) */
const TikTokIcon = ({ className = "w-5 h-5" }) => (
  <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M9 3v11.5a3.5 3.5 0 1 1-3.5-3.5" />
    <path d="M15 3c0 3.31 2.69 6 6 6" />
    <path d="M15 3v10a5 5 0 0 1-5 5" />
  </svg>
);

const useReveal = () => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const n = ref.current; if (!n) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) { setInView(true); return; }
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setInView(true); obs.unobserve(n); } }, { threshold: 0.05 });
    obs.observe(n); return () => obs.disconnect();
  }, []);
  return { ref, inView };
};

const EXPLORE = [
  { label: "Home", to: "/" },
  { label: "Our Story", to: "/about" },
  { label: "Pricing", to: "/pricing" },
  { label: "Our Guides", to: "/what-we-offer/guides" },
  { label: "Maya", to: "/what-we-offer/maya" },
  { label: "University-Specific Mock MMI", to: "/what-we-offer/mock-mmi" },
];

const SUPPORT = [
  { label: "Privacy Policy", to: "/privacy-policy" },
  { label: "Terms & Conditions", to: "/terms-and-conditions" },
  { label: "Cookie Policy", to: "/cookie-policy" },
  { label: "Refund & Cancellation Policy", to: "/refund-cancellation-policy" },
  { label: "Cookie Preferences", action: "open-cookie-banner" },
];

/* Real Medily social URLs. LinkedIn is intentionally inactive until the
   official account URL is provided. */
const SOCIAL = [
  { name: "Instagram", href: "https://www.instagram.com/medilyltd/", Icon: Instagram, testid: "footer-social-instagram" },
  { name: "TikTok", href: "https://tiktok.com/@medily.education", Icon: TikTokIcon, testid: "footer-social-tiktok" },
  { name: "Facebook", href: "https://facebook.com/profile.php?id=61592693884784", Icon: Facebook, testid: "footer-social-facebook" },
  { name: "LinkedIn", href: "https://www.linkedin.com/in/medilyeducation/", Icon: Linkedin, testid: "footer-social-linkedin" },
];

const ColumnHeading = ({ children }) => (
  <p className="text-[11.5px] font-bold tracking-[0.24em] uppercase text-slate-400">{children}</p>
);

const FooterLink = ({ to, external, children, testid }) =>
  external ? (
    <a
      href={to}
      target="_blank"
      rel="noopener noreferrer"
      className="group relative inline-block text-[14.5px] font-medium text-slate-600 hover:text-[#1d4ed8] transition-colors duration-300"
      data-testid={testid}
    >
      {children}
      <span className="absolute left-0 -bottom-0.5 h-px w-0 bg-[#1d4ed8] transition-all duration-300 group-hover:w-full" />
    </a>
  ) : (
    <Link
      to={to}
      className="group relative inline-block text-[14.5px] font-medium text-slate-600 hover:text-[#1d4ed8] transition-colors duration-300"
      data-testid={testid}
    >
      {children}
      <span className="absolute left-0 -bottom-0.5 h-px w-0 bg-[#1d4ed8] transition-all duration-300 group-hover:w-full" />
    </Link>
  );

const Footer = () => {
  const { ref, inView } = useReveal();
  /**
   * Atmospheric gradient — soft, blended, no hard boundary:
   *   • vertical white → very pale blue → soft blue
   *   • two radial glows anchored in the bottom-left and bottom-right corners
   *     (slightly deeper blue there, matches the reference)
   */
  const gradient = {
    backgroundImage: [
      "radial-gradient(ellipse 60% 55% at 0% 100%, rgba(147,177,240,0.45) 0%, rgba(147,177,240,0) 65%)",
      "radial-gradient(ellipse 60% 55% at 100% 100%, rgba(147,177,240,0.45) 0%, rgba(147,177,240,0) 65%)",
      "radial-gradient(ellipse 90% 55% at 50% 100%, rgba(199,215,247,0.35) 0%, rgba(199,215,247,0) 70%)",
      "linear-gradient(180deg, #ffffff 0%, #ffffff 30%, #f4f7fd 70%, #eaf0fb 100%)",
    ].join(", "),
  };
  return (
    <footer
      ref={ref}
      className="relative pt-16 md:pt-20"
      data-testid="site-footer"
      style={{
        ...gradient,
        opacity: inView ? 1 : 0,
        transform: inView ? "translate3d(0,0,0)" : "translate3d(0,20px,0)",
        transition: "opacity 800ms cubic-bezier(0.22,1,0.36,1), transform 900ms cubic-bezier(0.22,1,0.36,1)",
      }}
    >
      {/* Subtle divider separating page content from the footer */}
      <div className="max-w-7xl mx-auto px-6">
        <div className="h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent" />
      </div>

      <div className="max-w-7xl mx-auto px-6 pt-16 md:pt-20 pb-10">
        {/* ================= Brand row (logo + motto) ================= */}
        <div>
          <Link to="/" className="inline-flex items-center gap-2" data-testid="footer-logo">
            <img src={LOGO_URL} alt="Medily" className="h-8 md:h-9" />
          </Link>
          <h2
            className="mt-6 max-w-[720px] text-[38px] md:text-[56px] lg:text-[60px] font-semibold text-slate-900 tracking-tight leading-[1.05]"
            data-testid="footer-heading"
          >
            Preparing the{" "}
            <span className="relative inline-block">
              <span className="relative z-10 text-[#1d4ed8]">next generation</span>
              <span className="absolute left-0 right-0 bottom-1 h-2.5 bg-[#dbe6ff]/70 -z-0 rounded-sm" />
            </span>{" "}
            of doctors.
          </h2>
        </div>

        {/* ================= Column row ================= */}
        <div className="mt-16 md:mt-24 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-y-12 gap-x-8 lg:gap-x-16">
          {/* Explore */}
          <div>
            <ColumnHeading>Explore</ColumnHeading>
            <ul className="mt-5 space-y-3">
              {EXPLORE.map((l) => (
                <li key={l.to}>
                  <FooterLink to={l.to} testid={`footer-explore-${l.label.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "")}`}>
                    {l.label}
                  </FooterLink>
                </li>
              ))}
            </ul>
          </div>

          {/* Support */}
          <div>
            <ColumnHeading>Support</ColumnHeading>
            <ul className="mt-5 space-y-3">
              {SUPPORT.map((l) => {
                const testid = `footer-support-${l.label
                  .toLowerCase()
                  .replace(/\s+/g, "-")
                  .replace(/[^a-z0-9-]/g, "")
                  .replace(/-+/g, "-")
                  .replace(/^-|-$/g, "")}`;
                if (l.action === "open-cookie-banner") {
                  return (
                    <li key={l.label}>
                      <button
                        type="button"
                        onClick={() => window.dispatchEvent(new Event(OPEN_COOKIE_BANNER_EVENT))}
                        className="group relative inline-block text-[14.5px] font-medium text-slate-600 hover:text-[#1d4ed8] transition-colors duration-300 text-left"
                        data-testid={testid}
                      >
                        {l.label}
                        <span className="absolute left-0 -bottom-0.5 h-px w-0 bg-[#1d4ed8] transition-all duration-300 group-hover:w-full" />
                      </button>
                    </li>
                  );
                }
                return (
                  <li key={l.to}>
                    <FooterLink to={l.to} testid={testid}>
                      {l.label}
                    </FooterLink>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Connect (social icons) */}
          <div>
            <ColumnHeading>Connect</ColumnHeading>
            <ul className="mt-5 flex items-center gap-3">
              {SOCIAL.map(({ name, href, Icon, testid }) => (
                <li key={name}>
                  {href ? (
                    <a
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={name}
                      title={name}
                      className="inline-flex w-10 h-10 rounded-2xl bg-white border border-slate-100 text-slate-500 items-center justify-center shadow-[0_10px_25px_-15px_rgba(29,78,216,0.35)] hover:text-white hover:bg-[#1d4ed8] hover:border-[#1d4ed8] hover:-translate-y-0.5 hover:shadow-[0_20px_40px_-15px_rgba(29,78,216,0.55)] transition-all duration-300"
                      data-testid={testid}
                    >
                      <Icon className="w-[18px] h-[18px]" />
                    </a>
                  ) : (
                    <span
                      aria-label={`${name} (coming soon)`}
                      title={`${name} — coming soon`}
                      className="inline-flex w-10 h-10 rounded-2xl bg-white border border-slate-100 text-slate-300 items-center justify-center cursor-not-allowed opacity-60"
                      data-testid={testid}
                    >
                      <Icon className="w-[18px] h-[18px]" />
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Get in touch */}
          <div>
            <ColumnHeading>Get in touch</ColumnHeading>
            <ul className="mt-5 space-y-3">
              <li className="flex items-start gap-2.5">
                <span className="mt-0.5 inline-flex w-7 h-7 rounded-lg bg-[#eef4ff] text-[#1d4ed8] items-center justify-center">
                  <Mail className="w-3.5 h-3.5" />
                </span>
                <FooterLink to="mailto:info@medily.co" external testid="footer-email">
                  info@medily.co
                </FooterLink>
              </li>
            </ul>
          </div>
        </div>

        {/* ================= Bottom row ================= */}
        <div className="mt-16 md:mt-20 pt-6 border-t border-slate-200/60 flex flex-col md:flex-row items-center justify-between gap-3">
          <p className="text-[12.5px] text-slate-500" data-testid="footer-copyright">
            © Medily Ltd 2026. All rights reserved.
          </p>
          <p className="text-[12.5px] text-slate-500" data-testid="footer-tagline">
            Made for future doctors.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
